<template>
	<div>
		<v-data-table
			fixed-header
			dense
			:no-data-text="$t('no-data-available')"
			:loading-text="$t('loading')"
			:loading="loading"
			:search="appBarSearch"
			:headers="headers"
			:items="myPCCOrganizations"
			sort-by="id"
			:height="pageHeight(174)"
			ref="table"
			:items-per-page="-1"
			single-expand
			:footer-props="getDataTableFooterProps('officesTable')">
			<template #item="{ item, index, expand, isExpanded }">
				<tr :class="[getRowsForDataTable(index), { 'expanded-row': isExpanded }]" @click="expand(!isExpanded)">
					<td>
						<div class="d-flex align-center justify-start">
							<v-btn small icon>
								<v-icon small color="primary">
									{{ isExpanded ? 'mdi-arrow-down-drop-circle' : 'mdi-arrow-right-drop-circle' }}
								</v-icon>
							</v-btn>
							{{ item.pccOrganizationName }}
						</div>
					</td>
					<td class="text-left">{{ item.orgUuid }}</td>
					<td class="text-left">{{ item.scopeDescription }}</td>
					<td class="text-right">
						<v-btn
							v-if="item.pccCompanyMapping === null"
							x-small
							color="primary"
							@click.stop="openMapOrganizationDrawer(item)">
							{{ $t('map-organization') }}
						</v-btn>
						<v-menu
							v-else-if="item.pointClickCareFacilitiesDto.every((h) => h.pccLocationsMapping.length === 0)"
							left
							bottom
							class="py-0"
							offset-y
							transition="slide-x-transition">
							<template #activator="{ on, attrs }">
								<v-btn icon color="main_black" v-bind="attrs" v-on="on">
									<v-icon>mdi-dots-horizontal</v-icon>
								</v-btn>
							</template>
							<v-list class="px-0" dense>
								<v-list-item class="py-0 my-0" link dense @click="viewOrganizationMapping(item)">
									<v-list-item-icon class="mr-1">
										<v-icon class="icon-img mdi-18px" color="black">mdi-eye</v-icon>
									</v-list-item-icon>
									<v-list-item-content class="font-bold font-size14">
										{{ $t('view') }}
									</v-list-item-content>
								</v-list-item>
								<v-divider />
								<v-list-item link dense @click="openDeleteDialogOrg(item)">
									<v-list-item-icon class="mr-1">
										<v-icon color="main_red">mdi-delete</v-icon>
									</v-list-item-icon>
									<v-list-item-content class="font-bold font-size14">
										{{ $t('delete') }}
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</v-menu>
						<v-tooltip left v-else>
							<template #activator="{ on }">
								<v-icon color="primary" :size="25" v-on="on" @click.stop="viewOrganizationMapping(item)">
									mdi-eye
								</v-icon>
							</template>
							<span>{{ $t('view') }}</span>
						</v-tooltip>
					</td>
				</tr>
			</template>
			<template #expanded-item="{ item: org }">
				<tr style="cursor: default; background-color: #fff !important">
					<td class="pa-3" colspan="4">
						<v-data-table
							:items="org.pointClickCareFacilitiesDto"
							:headers="facilityHeaders"
							dense
							:items-per-page="-1"
							hide-default-footer
							class="data-table-outline"
							:height="pageHeight(83)"
							fixed-header>
							<template #[`item.facilityStatus`]="{ item }">
								<v-icon :color="item.facilityStatus === 'Live' ? 'green' : 'secondary'">mdi-circle</v-icon>
							</template>
							<template #[`item.phone`]="{ item }">
								{{ item.phone || '-' }}
							</template>
							<template #[`item.bedCount`]="{ item }">
								{{ item.bedCount || '-' }}
							</template>
							<template #[`item.actions`]="{ item }">
								<v-menu
									v-if="item.pccLocationsMapping.length === 0 && org.pccCompanyMapping"
									left
									bottom
									class="py-0"
									offset-y
									transition="slide-x-transition">
									<template #activator="{ on, attrs }">
										<v-btn icon color="main_black" v-bind="attrs" v-on="on">
											<v-icon>mdi-dots-horizontal</v-icon>
										</v-btn>
									</template>
									<v-list class="px-0" dense>
										<v-list-item
											class="py-0 my-0"
											link
											dense
											@click="openMapLocationDrawer({ ...item, orgId: org.pccCompanyMapping.mappedCompanyId })">
											<v-btn x-small color="primary" style="width: 100%">
												{{ $t('map-location') }}
											</v-btn>
										</v-list-item>
										<template v-if="!locationAlreadyExists(item.facilityName, org.pccCompanyMapping.mappedCompanyId)">
											<v-divider />
											<v-list-item link dense @click="checkLocation(item)">
												<v-list-item-content class="font-bold font-size14">
													<v-btn x-small color="primary" style="width: 100%">
														{{ $t('create-location') }}
													</v-btn>
												</v-list-item-content>
											</v-list-item>
										</template>
									</v-list>
								</v-menu>
								<v-tooltip left v-else-if="org.pccCompanyMapping">
									<template #activator="{ on }">
										<v-icon color="primary" :size="25" v-on="on" @click.stop="openDeleteDialogLocation(item)">
											mdi-delete
										</v-icon>
									</template>
									<span>{{ $t('delete') }}</span>
								</v-tooltip>
							</template>
						</v-data-table>
					</td>
				</tr>
			</template>
		</v-data-table>
		<DeleteDialog
			:dialog.sync="showDeleteDialogOrg"
			@confirm="
				showDeleteDialogOrg = false;
				deleteOrgMapping();
			" />
		<DeleteDialog
			:dialog.sync="showDeleteDialogLocation"
			@confirm="
				showDeleteDialogLocation = false;
				deleteLocationMapping();
			" />
	</div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { getDataTableFooterProps, getRowsForDataTable } from '@/utils/utilities';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';

export default {
	components: { DeleteDialog },
	data () {
		return {
			loading: true,
			showDeleteDialogOrg: false,
			showDeleteDialogLocation: false,
			selectedOrgId: null,
			selectedLocationId: null,
		};
	},
	computed: {
		...mapState({
			hospitals: (state) => state.hospitals.hospitals,
			myPCCOrganizations: (state) => state.pcc.myPCCOrganizations,
		}),
		...mapGetters({
			roleName: 'authentication/getRole',
			pageHeight: 'pageHeight',
			appBarSearch: 'filterbar/getSearch',
		}),
		detailsDrawer () {
			return this.$store.state.detailsDrawer;
		},
		headers () {
			return [
				{ text: this.$t('organization-name'), value: 'pccOrganizationName' },
				{ text: this.$t('org-id'), value: 'orgUuid' },
				{ text: this.$t('scope'), value: 'scopeDescription' },
				{ text: '', value: 'actions', sortable: false },
			];
		},
		facilityHeaders () {
			return [
				{ text: this.$t('status'), value: 'facilityStatus' },
				{ text: this.$t('facility-name'), value: 'facilityName' },
				{ text: this.$t('country'), value: 'country' },
				{ text: this.$t('phone'), value: 'phone' },
				{ text: this.$t('bed-count'), value: 'bedCount' },
				{ text: '', value: 'actions', sortable: false, align: 'end' },
			];
		},
	},
	watch: {
		detailsDrawer (val) {
			if (val === false) {
				this.loading = true;
				this.getMyPCCOrganizations();
				this.loading = false;
			}
		},
	},
	methods: {
		getRowsForDataTable,
		...mapActions({
			getPCCFacilityById: 'pcc/getPCCFacilityById',
			getMyPCCOrganizations: 'pcc/getMyPCCOrganizations',
			deletePccMapForCompany: 'pcc/deletePccMapForCompany',
			pccLocationDeleteMapping: 'pcc/pccLocationDeleteMapping',
		}),
		openDeleteDialogOrg (item) {
			this.showDeleteDialogOrg = true;
			this.selectedOrgId = item.pccCompanyMapping.id;
		},
		openDeleteDialogLocation (item) {
			this.showDeleteDialogLocation = true;
			this.selectedLocationId = item.pccLocationsMapping[0].id;
		},
		getDataTableFooterProps,
		async viewOrganizationMapping (item) {
			await this.$store.commit('mapping/SELECTED_ORGANIZATION', item);
			if (item !== null) {
				this.$store.commit('SET_EDIT_DETAILS_DRAWER', true);
			}
		},
		async deleteOrgMapping () {
			this.loading = true;
			await this.deletePccMapForCompany(this.selectedOrgId);
			this.getMyPCCOrganizations();
			this.loading = false;
		},
		async deleteLocationMapping () {
			this.loading = true;
			await this.pccLocationDeleteMapping(this.selectedLocationId);
			this.getMyPCCOrganizations();
			this.loading = false;
		},
		async checkLocation (item) {
			const pccFacilityById = await this.getPCCFacilityById({ pccOrgId: item.orgUuid, facilitiesById: item.facId });
			const isLocationMapped = pccFacilityById.pccLocationsMapping.length > 0;
			if (!isLocationMapped) {
				await this.$store.commit('mapping/SELECTED_ORGANIZATION', null);
				await this.$store.commit('mapping/SELECTED_LOCATION', pccFacilityById);
				await this.$store.commit('mapping/SET_SELECTED_PCC_LOCATIONS', pccFacilityById);

				this.$store.commit('SET_EDIT_DETAILS_DRAWER', true);
			}
		},
		locationAlreadyExists (facName, orgId) {
			return this.hospitals.find((h) => h.medicalInstitutionId === orgId && h.hospitalName === facName);
		},
		openMapOrganizationDrawer (item) {
			this.$store.commit('SET_DETAILS_DRAWER', {
				detailsDrawer: true,
				item,
				page: 'pointclickcare organization mapping',
			});
		},
		openMapLocationDrawer (item) {
			this.$store.commit('SET_DETAILS_DRAWER', {
				detailsDrawer: true,
				item,
				page: 'pointclickcare location mapping',
			});
		},
	},
	async mounted () {
		this.getMyPCCOrganizations();
		this.loading = false;
	},
};
</script>
